import React from 'react'
import { connect } from 'react-redux'
import { browserHistory } from 'react-router'
import { omitBy, isNil, toInteger } from 'lodash'

import { load, schemas } from 'api'
import { Page } from 'page'
import { applyHOCs, paginationPageCount } from 'helpers'
import { PageBlockRenderer } from 'core/components/PageBlockRenderer'
import { PageMain, PageSidebar, PageWithSidebar, Spacer } from 'layout'
import { loadFixedPageBlocks } from 'cms'

import {
  activeMonth,
  calendarBounds,
  calendarEvents,
  orderProgramsAndCourses,
  partitionBlocks,
  upcomingLiveEvents
} from './selectors'
import EventsLoading from './components/EventsLoading'
import ProgramsAndCoursesSection from './components/ProgramsAndCoursesSection'
import UpcomingEventsSection from './components/UpcomingEventsSection'

// The index list has no server-side paging — everything upcoming loads at
// once — so both lists are sliced on the client, 5 rows at a time, to keep
// what was becoming a very long page in check.
export const EVENTS_PER_PAGE = 5

export const EventsPage = ({ activeType, activeView, events, fixedPageBlocks, loadStatus, location }) => {
  const query = location.query || {}
  const blocks = partitionBlocks(fixedPageBlocks)

  const pushQuery = patch => {
    browserHistory.push({
      ...location,
      query: omitBy({ ...query, ...patch }, isNil)
    })
  }

  let content

  if (loadStatus.events.loading) {
    content = <EventsLoading />
  } else {
    // Bounds come from the unfiltered set so paging forward stays stable as
    // the reader changes the type filter.
    const bounds = calendarBounds(events)

    const liveEvents = upcomingLiveEvents(events, activeType)
    const eventsPage = toInteger(query.eventsPage) || 1
    const pagedEvents = liveEvents.slice(
      (eventsPage - 1) * EVENTS_PER_PAGE, eventsPage * EVENTS_PER_PAGE
    )

    const programs = orderProgramsAndCourses(events)
    const programsPage = toInteger(query.programsPage) || 1
    const pagedPrograms = programs.slice(
      (programsPage - 1) * EVENTS_PER_PAGE, programsPage * EVENTS_PER_PAGE
    )

    content = (
      <div>
        <UpcomingEventsSection
          events={pagedEvents}
          eventsPages={paginationPageCount(liveEvents.length, EVENTS_PER_PAGE)}
          calendarEvents={calendarEvents(events, activeType)}
          activeType={activeType}
          activeView={activeView}
          month={activeMonth(query.month, bounds)}
          bounds={bounds}
          query={query}
          // Changing the type filter changes which page 1 means, so land back on it.
          onDropdownChange={type => pushQuery({ type: type || null, eventsPage: 1 })}
          onMonthChange={month => pushQuery({ month })}
        />
        <ProgramsAndCoursesSection
          events={pagedPrograms}
          pages={paginationPageCount(programs.length, EVENTS_PER_PAGE)}
        />
      </div>
    )
  }

  return (
    <Page title='Events'>
      <PageWithSidebar>
        <PageMain>
          <PageBlockRenderer blocks={blocks.main} />
          {content}
          <Spacer size='md' />
        </PageMain>
        {
          // An empty sidebar would render as a bare grey column.
          blocks.sidebar.length > 0 &&
            <PageSidebar narrow>
              <PageBlockRenderer blocks={blocks.sidebar} />
            </PageSidebar>
        }
      </PageWithSidebar>
    </Page>
  )
}

EventsPage.propTypes = {
  events: React.PropTypes.arrayOf(React.PropTypes.object),
  fixedPageBlocks: React.PropTypes.array,
  activeType: React.PropTypes.string,
  activeView: React.PropTypes.oneOf(['index', 'calendar']).isRequired,
  location: React.PropTypes.object.isRequired,
  loadStatus: React.PropTypes.shape({
    events: React.PropTypes.shape({
      loading: React.PropTypes.bool
    }).isRequired
  }).isRequired
}

EventsPage.defaultProps = {
  events: [],
  fixedPageBlocks: []
}

export default applyHOCs([
  connect((state, props) => ({
    activeType: props.location.query.type,
    activeView: props.location.query.view === 'calendar' ? 'calendar' : 'index'
  })),
  load({
    events: {
      urlSelector: () => '/v2/events',
      schema: [schemas.event]
    }
  }),
  // The sidebar is not worth blocking the events list on, so this loads
  // alongside rather than gating the page.
  loadFixedPageBlocks({ pageSelector: () => 'events_page' })
])(EventsPage)
